import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import './block-edit.less';
import { ContextMenu, Input, MenuItem, MenuList, SideHeader, TnModal } from 'bimui';
import { useContext, useEffect, useRef, useState } from 'react';
import { deleteBlockMulti, findBlocksInProject } from '@/declare/api/geometry/block';
import { message } from 'antd';
import classNames from 'classnames';
import { emitter } from '@/utils/mitt';
import { Emit_Event_Common_Tool } from '@/declare/enums/emit-event';
import { BaseUtils } from 'tnbase';
import { Block } from '@/declare/interface/geometry/block';
import BlockDetailPanel from "@/layout/graphic/block";
import { EngineContext, RegisterContext } from "@/App";
import { ReactComponent as CustomizeSvg } from 'bimui/dist/c1/icons/customize.svg';
import { ReactComponent as ArrangeSvg } from 'bimui/dist/c1/icons/arrange-constraints.svg';
import { PythaEventBlock } from "pytha2";


/**
 * 块模板的编辑面板
 */
export default function BlockEditPanel() {

    const { projectUuid } = useParams();
    const navigate = useNavigate();
    const registerContext = useContext(RegisterContext);
    const engineContext = useContext(EngineContext);
    const location = useLocation();

    const [blocks, setBlocks] = useState<Block[]>([]);
    const [activeBlockUuid, setActiveBlockUuid] = useState<string>(null);   // 当前激活的块模板
    const [deleteEventUuid, setDeleteEventUuid] = useState<string>(null);
    // 右键
    const [cmOpen, setCmOpen] = useState<boolean>(false);
    const [cmTop, setCmTop] = useState<number>();
    const [cmLeft, setCmLeft] = useState<number>();

    const [loading, setLoading] = useState<boolean>(false);
    const [editUuid, setEditUuid] = useState<string>(null); // 编辑的uuid
    const [inputValue, setInputValue] = useState<string>(''); // 输入框的值

    useEffect(() => {
        // 关闭所有工具
        registerContext.activeTool(null);
        emitter.on(Emit_Event_Common_Tool.DELETE, onListenDelete);

        return () => {
            emitter.off(Emit_Event_Common_Tool.DELETE, onListenDelete);
        }
    }, [])

    useEffect(() => {
        if (projectUuid == null) return;
        generateData();
    }, [projectUuid])

    useEffect(() => {
        if (deleteEventUuid == null) return;
        onClickDelete();
    }, [deleteEventUuid])

    useEffect(() => {
        const path = location.pathname;
        const origin = path.split('/blocks/');
        const uuid = origin[1];
        setActiveBlockUuid(uuid);
    }, [location])

    // 监听删除事件
    const onListenDelete = () => {
        setDeleteEventUuid(BaseUtils.genUUID());
    }

    const generateData = () => {
        findBlocksInProject(projectUuid).then(res => {
            const list = res.data || [];
            setBlocks(list);
            const path = location.pathname;
            const origin = path.split('/blocks/');
            const uuid = origin[1];
            if (uuid == null && list.length > 0) {
                setActiveBlockUuid(list[0].uuid);
                if (origin[0].endsWith('/blocks')) {
                    navigate(`${origin[0]}/${list[0].uuid}`);
                } else {
                    navigate(`${origin[0]}/blocks/${list[0].uuid}`);
                }
            }
        }).catch(err => {
            message.error("获取块列表失败")
        })
    }

    // 删除
    const onClickDelete = () => {
        if (activeBlockUuid == null || !blocks || blocks.length == 0) return;
        setCmOpen(false);
        if (loading) return;
        TnModal().deleteModal({
            onOk: () => {
                setLoading(true);
                deleteBlockMulti([activeBlockUuid]).then(res => {
                    setActiveBlockUuid(null);
                    setBlocks(blocks.filter(b => b.uuid !== activeBlockUuid));
                }).catch(err => {
                    message.error("删除失败")
                }).finally(() => {
                    TnModal().destoryAll();
                    setLoading(false);
                })
            }
        })
    }

    // 点击数据行
    const onClickRow = (uuid) => {
        setActiveBlockUuid(uuid);
        navigate(`/projects/${projectUuid}/blocks/${uuid}`);
    }

    // 右键
    const onContextMenu = (e, uuid) => {
        e.preventDefault();
        e.stopPropagation();
        setActiveBlockUuid(uuid);
        setCmOpen(true);
        setCmLeft(e.clientX);
        setCmTop(e.clientY);
    }

    // 点击拾取点
    const onClickPickPoint = () => {
        // 发送一个信号给详情页，让详情页做编辑
        emitter.emit(Emit_Event_Common_Tool.PICK_POINT);
    }

    // 点击布置
    const onClickArrange = (e, uuid) => {
        e.preventDefault();
        e.stopPropagation();
        if (uuid === activeBlockUuid) {
            message.warning("无法布置当前编辑的块");
            return;
        }
        const block = blocks.find(b => uuid === b.uuid);
        if (!block) return;
        if (!engineContext.engine) return;
        engineContext?.emitCommand("ARRANGE_BLOCK");
        let event = new PythaEventBlock();
        event.data = { "blockTemplateUuid": block.uuid };
        engineContext?.engine?.eventMediator?.emitEvent(event);
    }

    return <div className="block-edit-panel">
        <div className="left">
            <SideHeader>
                <div className='header'>
                    <span>块</span>
                    {activeBlockUuid != null && <CustomizeSvg className='add-icon' onClick={onClickPickPoint} />}
                </div>
            </SideHeader>
            <div className='content'>
                {blocks.map(block => {
                    return <div key={block?.uuid} className='block-row'>
                        <div className={classNames({
                            "block-item": true,
                            'active': activeBlockUuid === block.uuid
                        })}>
                            {editUuid !== block?.uuid && <span className="name ellipsis"
                                onClick={(e) => onClickRow(block.uuid)}
                                onContextMenu={(e) => onContextMenu(e, block?.uuid)}>{block?.properties?.name}</span>}
                            {editUuid !== block?.uuid && <ArrangeSvg className="icon" onClick={(e) => onClickArrange(e, block.uuid)} />}
                        </div>
                        {editUuid === block?.uuid && <Input value={inputValue} autoFocus
                            onChange={(e) => setInputValue(e.target.value)} />}
                    </div>
                })}
            </div>
            <ContextMenu
                visible={cmOpen} // 是否显示
                top={cmTop} // 菜单纵坐标
                left={cmLeft} // 菜单横坐标
                wrapperClassname='section-list-right-menu' // 自定义右键菜单样式
                onCancel={() => {
                    setCmOpen(false); // 关闭右键菜单
                }}
            >
                <MenuList>
                    {/* <MenuItem onClick={onEdit}>
                    编辑
                </MenuItem> */}
                    <MenuItem onClick={onClickDelete} special>
                        删除
                    </MenuItem>
                </MenuList>
            </ContextMenu>
        </div>
        <div className="detail">
            <Routes>
                <Route path="/:blockUuid" element={<BlockDetailPanel />} />
            </Routes>
        </div>
    </div>
}